import { gql, useApolloClient, useMutation } from '@apollo/client';
import React, { useCallback, useContext, useMemo } from 'react';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IDeleteMutationSettings } from '../../../../../../Components/ObjectMenu/DeleteMutation';
import {
  TEDEKBokforderMinBokforderStatusEnum,
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
  TEDEKBokforderVariantEnum,
} from '../../../../../../globalQueryTypes';
import {
  IFunctionMenuItem,
  useItemFunctionMenuItemsType,
} from '../../../../../../Utils/ObjektVyUtils';
import BusinessDataSetContext from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataSetContext/BusinessDataSetContext';
import { StandardRutinContext } from '../../../../_StandardRutin/StandardRutinContext';
import {
  BokforderUpdateMutation,
  BokforderUpdateMutationVariables,
} from '../../../ObjektVyer/Bokforder/Mutations/_QueryTypes/BokforderUpdateMutation';
import { BokforderWrapperContext } from '../Contexts/BokforderWrapperContext';
import UpdateMutation from '../../../ObjektVyer/Bokforder/Mutations/BokforderMutation.gql';
import {
  IFormData,
  removeIdsFromNewAssociations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { BokforderType } from '../../../ObjektVyer/Bokforder/Queries/_QueryTypes/BokforderType';
import { UtanordningType } from '../../../ObjektVyer/Bokforder/Queries/_QueryTypes/UtanordningType';

const createDeleteOrUndeleteMutation = (
  collectionName?: string,
  database?: string,
  id?: string,
  action?: 'Delete' | 'Undelete',
  version?: number
): string => {
  const args = `(values:{id: "${id}", _version: ${version}})`;
  return `mutation ${action}Mutation {
	  db(name: "${database}") {
		${collectionName}_${action}${args} {
		  id
		  MinBokforderStatus
		  _version
		}
	  }
	}`;
};

const useDeleteMutation = (
  props: IDeleteMutationSettings,
  action: 'Delete' | 'Undelete'
): { deleteFunction: React.DispatchWithoutAction; loading: boolean } => {
  const mutation = gql`
    ${createDeleteOrUndeleteMutation(
      props.collectionName,
      props.dbName,
      props.id ?? undefined,
      action,
      props.version
    )}
  `;

  const [mutate, { loading }] = useMutation(mutation, {
    onCompleted: props.onCompleted,
  });
  const deleteFunction = useCallback(() => {
    mutate();
  }, [mutate]);
  return { deleteFunction, loading };
};

const useBokforderFunctionMenuItems: useItemFunctionMenuItemsType = input => {
  const apolloClient = useApolloClient();
  const { activeDatabase, userID } = useContext(ActiveDatabaseContext);
  const standardRutinContext = useContext(StandardRutinContext);
  const businessDataSetContext = useContext(BusinessDataSetContext);
  const refetch = businessDataSetContext.refetch;
  const { currentUser, currentUserDataLoading } = useContext(
    BokforderWrapperContext
  );

  const [updateMutation] = useMutation<
    BokforderUpdateMutation,
    BokforderUpdateMutationVariables
  >(UpdateMutation);

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values ?? {});
      return updateMutation({
        variables: {
          db: activeDatabase || '',
          values: {
            id: input?.id ?? '',
            _version: input.item?._version ?? 1,
            ...newValues,
          },
        },
      });
    },
    [updateMutation, activeDatabase, input?.id, input.item?._version]
  );

  const onCompletedMutation = useCallback(() => {
    apolloClient.refetchQueries({
      include: standardRutinContext.refetchButtonQueries,
    });
    refetch();
  }, [apolloClient, refetch, standardRutinContext.refetchButtonQueries]);

  const { deleteFunction } = useDeleteMutation(
    {
      collectionName: 'EKBokforder',
      dbName: activeDatabase ?? '',
      id: input.id,
      onCompleted: onCompletedMutation,
      version: input.item?._version ?? 1,
    },
    'Delete'
  );

  const onDeleteClick = useCallback(() => {
    if (
      input?.item?.MinBokforderStatus ===
      TEDEKBokforderMinBokforderStatusEnum.UTKAST
    ) {
      deleteFunction();
    } else {
      updateFunction({ STATUS: TEDEKBokforderSTATUSEnum.BORTTAGEN });
    }
  }, [deleteFunction, input?.item?.MinBokforderStatus, updateFunction]);

  const isDeletable = useMemo(
    () =>
      (input.item?.MinBokforderStatus ===
        TEDEKBokforderMinBokforderStatusEnum.UTKAST ||
        input.item?.MinBokforderStatus ===
          TEDEKBokforderMinBokforderStatusEnum.EJ_GODKAND_AV_ATTESTANT) &&
      input.item?._Associations?.Bestallare?.id === userID,
    [
      input.item?.MinBokforderStatus,
      input.item?._Associations?.Bestallare?.id,
      userID,
    ]
  );

  const canBeCopied = useMemo(
    () =>
      input.item?.REF_BOKF_AR == null &&
      input.item?.REF_VER_NR == null &&
      currentUser &&
      currentUser.BESTALLARE_BOKFORDER &&
      !currentUserDataLoading &&
      input.item?.TYP !== TEDEKBokforderTYPEnum.UTANORDNING,
    [
      currentUser,
      currentUserDataLoading,
      input.item?.REF_BOKF_AR,
      input.item?.REF_VER_NR,
      input.item?.TYP,
    ]
  );
  const getCopyButtonDisabledReason = useCallback(() => {
    if (input.item?.TYP === TEDEKBokforderTYPEnum.UTANORDNING) {
      return 'Kopiering av Utanordningar är tillfälligt avstängd';
    }
    if (input.item?.REF_BOKF_AR != null || input.item?.REF_VER_NR != null) {
      return 'Bokföringsordern har en ändringsverifikation kopplad till sig';
    }
    if (currentUser && !currentUser.BESTALLARE_BOKFORDER) {
      return 'Du har inte behörighet att kopiera bokföringsordrar';
    }
    return undefined;
  }, [
    currentUser,
    input.item?.REF_BOKF_AR,
    input.item?.REF_VER_NR,
    input.item?.TYP,
  ]);

  const buttons: IFunctionMenuItem[] = [
    {
      caption: 'Ta bort Bokföringsorder',
      id: 'delete',
      iconType: 'delete',
      disabled: !isDeletable,
      disabledReason: isDeletable
        ? undefined
        : 'Du kan endast ta bort bokföringsordrar som ligger som ej godkända av attestant samt utkast',
      onClick: onDeleteClick,
    },
    {
      caption:
        input.item?.TYP == TEDEKBokforderTYPEnum.UTANORDNING
          ? 'Kopiera Utanordningen'
          : 'Kopiera Bokföringsordern',
      id: 'copy',
      iconType: 'content_copy',
      disabled: !canBeCopied,
      disabledReason: canBeCopied ? undefined : getCopyButtonDisabledReason(),
      onClick: () => {
        const item = input.item as BokforderType | UtanordningType | null;
        businessDataSetContext.selectNewDetail(undefined, {
          type: item?.TYP
            ? TEDEKBokforderTYPEnum[item?.TYP]
            : TEDEKBokforderTYPEnum.BOKFORINGSORDER,
          variant: item?.Variant
            ? TEDEKBokforderVariantEnum[item?.Variant]
            : TEDEKBokforderVariantEnum.KOPIERA,
          copyID: input.id ?? '',
        });
      },
    },
  ];

  return buttons;
};

export default useBokforderFunctionMenuItems;
